// file imports
import { IReport } from '@/types';

const ReportImageComponent = ({ data }: { data: IReport }) => (
  <div className="w-[800px] min-h-[1000px] relative" style={{ backgroundColor: '#ffffff', borderRadius: '12px', overflow: 'hidden' }}>
    {/* Blue Header with Gradient */}
    <div className="p-6 rounded-t-xl" style={{ background: 'linear-gradient(to right, #2563eb, #1d4ed8)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Checkmark Icon */}
          <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#60a5fa' }}>
            <svg className="w-8 h-8" style={{ color: '#ffffff' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              Speaking Engagement Report
            </h1>
            <p className="text-sm mt-1" style={{ color: '#dbeafe' }}>
              Audience Feedback Report
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Main Content */}
    <div className="p-6 space-y-6">
      {/* Metrics Section */}
      {data.sessionInsights && (
        <div>
          <h2 className="text-lg font-bold mb-4" style={{ color: '#1f2937' }}>
            Metrics
          </h2>
          <div className="grid grid-cols-2 gap-4">
            {/* Session Value */}
            {data.sessionInsights.sessionValue && (
              <div className="p-3 border rounded-lg" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: '#d1fae5' }}></div>
                  <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>
                    Session Value
                  </span>
                </div>
                <div className="text-2xl font-bold" style={{ color: '#1f2937' }}>
                  {data.sessionInsights.sessionValue.count || 0}
                </div>
                <div className="text-xs" style={{ color: '#6b7280' }}>
                  {data.sessionInsights.sessionValue.percentage || '0%'} found valuable
                </div>
              </div>
            )}

            {/* Immediate Application */}
            {data.sessionInsights.immediateApplication && (
              <div className="p-3 border rounded-lg" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: '#dbeafe' }}></div>
                  <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>
                    Immediate Application
                  </span>
                </div>
                <div className="text-2xl font-bold" style={{ color: '#1f2937' }}>
                  {data.sessionInsights.immediateApplication.count || 0}
                </div>
                <div className="text-xs" style={{ color: '#6b7280' }}>
                  {data.sessionInsights.immediateApplication.percentage || '0%'} will apply
                </div>
              </div>
            )}

            {/* Engagement Level */}
            {data.sessionInsights.engagementLevel && (
              <div className="p-3 border rounded-lg" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: '#fed7aa' }}></div>
                  <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>
                    Engagement Level
                  </span>
                </div>
                <div className="text-2xl font-bold" style={{ color: '#1f2937' }}>
                  {data.sessionInsights.engagementLevel.count || 0}
                </div>
                <div className="text-xs" style={{ color: '#6b7280' }}>
                  {data.sessionInsights.engagementLevel.percentage || '0%'} highly engaged
                </div>
              </div>
            )}

            {/* Topic Relevance */}
            {data.sessionInsights.topicRelevance && (
              <div className="p-3 border rounded-lg" style={{ borderColor: '#e5e7eb' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded" style={{ backgroundColor: '#e9d5ff' }}></div>
                  <span className="text-sm font-semibold" style={{ color: '#1f2937' }}>
                    Topic Relevance
                  </span>
                </div>
                <div className="text-2xl font-bold" style={{ color: '#1f2937' }}>
                  {data.sessionInsights.topicRelevance.count || 0}
                </div>
                <div className="text-xs" style={{ color: '#6b7280' }}>
                  {data.sessionInsights.topicRelevance.percentage || '0%'} found relevant
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NPS Score */}
      {data.sessionInsights?.npsScore && (
        <div className="p-4 rounded-lg" style={{ background: 'linear-gradient(to right, #dbeafe, #e0e7ff)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold" style={{ color: '#1f2937' }}>
              Net Promoter Score (NPS)
            </h3>
            <div className="text-2xl font-bold" style={{ color: '#2563eb' }}>
              {data.sessionInsights.npsScore.average?.toFixed(1) || '0.0'}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-1" style={{ color: '#6b7280' }}>
                <span>Not likely (0)</span>
                <span>Very likely (10)</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                <div className="h-full rounded-full" style={{ width: `${(data.sessionInsights.npsScore.average || 0) * 10}%`, background: 'linear-gradient(to right, #ef4444, #eab308, #22c55e)' }}></div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs" style={{ color: '#6b7280' }}>
                Based on
              </div>
              <div className="text-sm font-semibold" style={{ color: '#1f2937' }}>
                {data.sessionInsights.npsScore.count || 0} responses
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Session Info Section */}
      {data.sessionInfo && (
        <div>
          <h2 className="text-lg font-bold mb-4" style={{ color: '#3b82f6' }}>
            Attendees found this session
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-4 h-5">
              <span className="w-24 text-sm font-medium leading-none" style={{ color: '#374151' }}>
                {data.sessionInfo.interactive?.label || 'Interactive'}
              </span>
              <div className="flex-1 flex items-center">
                <div className="relative h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: data.sessionInfo.interactive?.percentage || '0%', backgroundColor: '#ef4444' }}></div>
                </div>
              </div>
              <span className="w-12 text-sm font-semibold text-right leading-none" style={{ color: '#1f2937' }}>
                {data.sessionInfo.interactive?.percentage || '0%'}
              </span>
            </div>
            <div className="flex items-center gap-4 h-5">
              <span className="w-24 text-sm font-medium leading-none" style={{ color: '#374151' }}>
                {data.sessionInfo.actionable?.label || 'Actionable'}
              </span>
              <div className="flex-1 flex items-center">
                <div className="relative h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: data.sessionInfo.actionable?.percentage || '0%', backgroundColor: '#22c55e' }}></div>
                </div>
              </div>
              <span className="w-12 text-sm font-semibold text-right leading-none" style={{ color: '#1f2937' }}>
                {data.sessionInfo.actionable?.percentage || '0%'}
              </span>
            </div>
            <div className="flex items-center gap-4 h-5">
              <span className="w-24 text-sm font-medium leading-none" style={{ color: '#374151' }}>
                {data.sessionInfo.inspiring?.label || 'Inspiring'}
              </span>
              <div className="flex-1 flex items-center">
                <div className="relative h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: data.sessionInfo.inspiring?.percentage || '0%', backgroundColor: '#f97316' }}></div>
                </div>
              </div>
              <span className="w-12 text-sm font-semibold text-right leading-none" style={{ color: '#1f2937' }}>
                {data.sessionInfo.inspiring?.percentage || '0%'}
              </span>
            </div>
            <div className="flex items-center gap-4 h-5">
              <span className="w-24 text-sm font-medium leading-none" style={{ color: '#374151' }}>
                {data.sessionInfo.relevant?.label || 'Relevant'}
              </span>
              <div className="flex-1 flex items-center">
                <div className="relative h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: data.sessionInfo.relevant?.percentage || '0%', backgroundColor: '#3b82f6' }}></div>
                </div>
              </div>
              <span className="w-12 text-sm font-semibold text-right leading-none" style={{ color: '#1f2937' }}>
                {data.sessionInfo.relevant?.percentage || '0%'}
              </span>
            </div>
            <div className="flex items-center gap-4 h-5">
              <span className="w-24 text-sm font-medium leading-none" style={{ color: '#374151' }}>
                {data.sessionInfo.engaging?.label || 'Engaging'}
              </span>
              <div className="flex-1 flex items-center">
                <div className="relative h-2 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#e5e7eb' }}>
                  <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: data.sessionInfo.engaging?.percentage || '0%', backgroundColor: '#a855f7' }}></div>
                </div>
              </div>
              <span className="w-12 text-sm font-semibold text-right leading-none" style={{ color: '#1f2937' }}>
                {data.sessionInfo.engaging?.percentage || '0%'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Memorable Insights & Speaker Styles */}
      {data.sessionInsights && ((data.sessionInsights.memorableInsights && data.sessionInsights.memorableInsights.length > 0) || (data.sessionInsights.speakerStyles && data.sessionInsights.speakerStyles.length > 0)) && (
        <div className="grid grid-cols-2 gap-4">
          {/* Memorable Insights */}
          {data.sessionInsights.memorableInsights && data.sessionInsights.memorableInsights.length > 0 && (
            <div>
              <h3 className="text-base font-semibold mb-3" style={{ color: '#1f2937' }}>
                Memorable Insights
              </h3>
              <div className="space-y-2">
                {data.sessionInsights.memorableInsights.slice(0, 3).map((insight: string, index: number) => (
                  <div key={index} className="p-2 rounded border" style={{ backgroundColor: '#fefce8', borderColor: '#eab308' }}>
                    <p className="text-xs italic" style={{ color: '#374151' }}>
                      &ldquo;{insight}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Speaker Styles */}
          {data.sessionInsights.speakerStyles && data.sessionInsights.speakerStyles.length > 0 && (
            <div>
              <h3 className="text-base font-semibold mb-3" style={{ color: '#1f2937' }}>
                Speaker Styles
              </h3>
              <div className="space-y-2">
                {data.sessionInsights.speakerStyles.slice(0, 3).map((style: string, index: number) => (
                  <div key={index} className="p-2 rounded border" style={{ backgroundColor: '#faf5ff', borderColor: '#a855f7' }}>
                    <p className="text-xs italic" style={{ color: '#374151' }}>
                      &ldquo;{style}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Suggestions */}
      {data.suggestions && data.suggestions.length > 0 && (
        <div>
          <h3 className="text-base font-semibold mb-3" style={{ color: '#1f2937' }}>
            Additional Feedback
          </h3>
          <div className="space-y-2">
            {data.suggestions.slice(0, 3).map((suggestion: string, index: number) => (
              <div key={index} className="p-2 rounded" style={{ backgroundColor: '#f9fafb' }}>
                <p className="text-xs" style={{ color: '#374151' }}>
                  {suggestion}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    {/* Footer */}
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
      <div className="text-lg font-bold" style={{ color: '#1f2937' }}>
        TBOS
      </div>
    </div>
  </div>
);

export default ReportImageComponent;
